// client/src/components/pages/AmendmentsAllocateTransactions/DynamicFilters.tsx
// (converted from .js, ST-00221). The "Dynamic Column Filters" block of the Amendments filters.
import { useState } from 'react';
import {
  XMarkIcon,
  AdjustmentsHorizontalIcon,
  ChevronDownIcon,
  ChevronUpIcon,
} from '@heroicons/react/20/solid';
import DynamicColumnSelector, {
  AVAILABLE_COLUMNS,
  type DynamicColumn,
  type DynamicColumnType,
} from './DynamicColumnSelector';
import ThemedSelect from '../../common/ThemedSelect';
import UnifiedDatePicker from '../../common/UnifiedDatePicker';
import { toApiDateString } from '../../../utils/dateUtils';

/** One dynamic filter as the panel keeps it (and saves it in localStorage). */
export interface DynamicFilter {
  id: number;
  column: string;
  label: string;
  type: DynamicColumnType;
  operator: string;
  value: string;
  category: string;
}

/** The operators that take no value. */
export const VALUELESS_OPERATORS: ReadonlySet<string> = new Set(['IsEmpty', 'IsNotEmpty']);

/**
 * Whether a filter applies. "Is Empty" / "Is Not Empty" have no value, and every filter with
 * an empty value used to be dropped, so they never applied (AMD-24); they are sent with ''.
 */
export const isDynamicFilterActive = (filter: DynamicFilter): boolean =>
  VALUELESS_OPERATORS.has(filter.operator) ||
  (typeof filter.value === 'string' && filter.value.trim() !== '');

const KNOWN_COLUMNS: ReadonlySet<string> = new Set(AVAILABLE_COLUMNS.map((c) => c.name));

/** Saved filters (localStorage) on columns that are still offered; "Is Active Amendment" is not. */
export const knownDynamicFilters = (saved: unknown): DynamicFilter[] =>
  Array.isArray(saved)
    ? (saved as DynamicFilter[]).filter(
        (f) => !!f && typeof f === 'object' && KNOWN_COLUMNS.has(f.column)
      )
    : [];

const FILTER_TYPES: readonly { value: string; label: string }[] = [
  { value: 'Contains', label: 'Contains' },
  { value: 'StartsWith', label: 'Starts With' },
  { value: 'EndsWith', label: 'Ends With' },
  { value: 'Equals', label: 'Equals' },
  { value: 'NotEquals', label: 'Not Equals' },
  { value: 'IsEmpty', label: 'Is Empty' },
  { value: 'IsNotEmpty', label: 'Is Not Empty' },
  { value: 'GreaterThan', label: 'Greater Than' },
  { value: 'LessThan', label: 'Less Than' },
  { value: 'GreaterEqual', label: 'Greater or Equal' },
  { value: 'LessEqual', label: 'Less or Equal' },
];

const COMPARISON_OPERATORS = [
  'Equals',
  'NotEquals',
  'GreaterThan',
  'LessThan',
  'GreaterEqual',
  'LessEqual',
  'IsEmpty',
  'IsNotEmpty',
];

const getAvailableFilterTypes = (columnType: DynamicColumnType) => {
  switch (columnType) {
    case 'number':
    case 'date':
    case 'datetime':
      return FILTER_TYPES.filter((type) => COMPARISON_OPERATORS.includes(type.value));
    case 'boolean':
      return FILTER_TYPES.filter((type) =>
        ['Equals', 'NotEquals', 'IsEmpty', 'IsNotEmpty'].includes(type.value)
      );
    default: // text
      return FILTER_TYPES;
  }
};

const getDefaultOperator = (columnType: DynamicColumnType) => {
  switch (columnType) {
    case 'number':
    case 'boolean':
    case 'date':
    case 'datetime':
      return 'Equals';
    default:
      return 'Contains';
  }
};

const pad = (n: number) => String(n).padStart(2, '0');

/** 'YYYY-MM-DD' or 'YYYY-MM-DDTHH:mm' (local) → Date; anything else → null. */
const parseFilterDate = (value: string): Date | null => {
  const m = /^(\d{4})-(\d{2})-(\d{2})(?:T(\d{2}):(\d{2}))?/.exec(value || '');
  if (!m) return null;
  const date = new Date(
    Number(m[1]),
    Number(m[2]) - 1,
    Number(m[3]),
    Number(m[4] ?? 0),
    Number(m[5] ?? 0)
  );
  return isNaN(date.getTime()) ? null : date;
};

/**
 * The value the server takes: 'YYYY-MM-DD' for a date, the picked minute 'YYYY-MM-DDTHH:mm'
 * for a timestamp (the format the native datetime-local input used to send).
 */
const formatFilterDate = (date: Date | null, withTime: boolean): string => {
  if (!date) return '';
  const day = toApiDateString(date);
  return withTime ? `${day}T${pad(date.getHours())}:${pad(date.getMinutes())}` : day;
};

const CATEGORY_COLORS: Readonly<Record<string, string>> = {
  basic: 'bg-primary-100 text-primary-800',
  location: 'bg-green-100 text-green-800',
  weight: 'bg-purple-100 text-purple-800',
  transport: 'bg-orange-100 text-orange-800',
  product: 'bg-pink-100 text-pink-800',
  security: 'bg-red-100 text-red-800',
  timestamp: 'bg-primary-100 text-primary-800',
  tags: 'bg-yellow-100 text-yellow-800',
  amendment: 'bg-gray-100 text-gray-800',
  system: 'bg-slate-100 text-slate-800',
};

const getCategoryColor = (category: string) =>
  CATEGORY_COLORS[category] || 'bg-gray-100 text-gray-800';

export interface DynamicFiltersProps {
  dynamicFilters: DynamicFilter[];
  onDynamicFilterChange: (filterId: number, field: 'operator' | 'value', value: string) => void;
  onRemoveDynamicFilter: (filterId: number) => void;
  onAddDynamicFilter: (filter: DynamicFilter) => void;
}

const inputClass =
  'flex-1 px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500';

const DynamicFilters = ({
  dynamicFilters,
  onDynamicFilterChange,
  onRemoveDynamicFilter,
  onAddDynamicFilter,
}: DynamicFiltersProps) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const handleFilterChange = (filterId: number, field: 'operator' | 'value', value: string) => {
    onDynamicFilterChange(filterId, field, value);
  };

  const handleAddFilter = (column: DynamicColumn) => {
    onAddDynamicFilter({
      id: Date.now(),
      column: column.name,
      label: column.label,
      type: column.type,
      operator: getDefaultOperator(column.type),
      value: '',
      category: column.category,
    });
  };

  const renderFilterInput = (filter: DynamicFilter) => {
    if (VALUELESS_OPERATORS.has(filter.operator)) {
      return null;
    }

    switch (filter.type) {
      case 'number':
        return (
          <input
            type="number"
            value={filter.value}
            onChange={(e) => handleFilterChange(filter.id, 'value', e.target.value)}
            className={inputClass}
            placeholder="Enter number..."
          />
        );
      case 'date':
      case 'datetime': {
        // The shared picker (CLAUDE.md), not a native date input.
        const withTime = filter.type === 'datetime';
        return (
          <div className="flex-1">
            <UnifiedDatePicker
              value={parseFilterDate(filter.value)}
              onChange={(date) =>
                handleFilterChange(filter.id, 'value', formatFilterDate(date, withTime))
              }
              withTime={withTime}
              clearable
              aria-label={`${filter.label} value`}
              placeholder={withTime ? 'yyyy/mm/dd hh:mm' : 'yyyy/mm/dd'}
              className={`w-full ${inputClass}`}
              wrapperClassName="w-full"
            />
          </div>
        );
      }
      case 'boolean':
        return (
          <ThemedSelect
            value={filter.value}
            onChange={(e) => handleFilterChange(filter.id, 'value', String(e.target.value))}
            className="flex-1 px-3 py-2 text-sm border border-gray-300 bg-surface rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          >
            <option value="">Select...</option>
            <option value="1">True</option>
            <option value="0">False</option>
          </ThemedSelect>
        );
      default: // text
        return (
          <input
            type="text"
            value={filter.value}
            onChange={(e) => handleFilterChange(filter.id, 'value', e.target.value)}
            className={inputClass}
            placeholder="Enter text..."
          />
        );
    }
  };

  const activeFilterColumns = dynamicFilters.map((filter) => filter.column);

  return (
    <div className="bg-gray-50 rounded-lg p-4 border border-gray-200 space-y-4">
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-medium text-gray-900 flex items-center">
          <AdjustmentsHorizontalIcon className="h-4 w-4 mr-2 text-primary-600" />
          Dynamic Column Filters
          {dynamicFilters.length > 0 && (
            <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-primary-100 text-primary-800">
              {dynamicFilters.length}
            </span>
          )}
        </h4>
        <div className="flex items-center space-x-2">
          <DynamicColumnSelector
            onAddFilter={handleAddFilter}
            activeFilters={activeFilterColumns}
          />
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="text-gray-400 hover:text-gray-600 focus:outline-none"
          >
            {isExpanded ? (
              <ChevronUpIcon className="h-4 w-4" />
            ) : (
              <ChevronDownIcon className="h-4 w-4" />
            )}
          </button>
        </div>
      </div>

      {isExpanded && (
        <>
          {/* Active Dynamic Filters */}
          {dynamicFilters.length > 0 ? (
            <div className="space-y-3">
              {dynamicFilters.map((filter) => (
                <div key={filter.id} className="bg-surface p-3 rounded-lg border border-gray-200">
                  <div className="flex items-center space-x-3">
                    {/* Column Label with Category Badge */}
                    <div className="flex items-center space-x-2 min-w-0 flex-1">
                      <span
                        className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getCategoryColor(filter.category)}`}
                      >
                        {filter.category}
                      </span>
                      <span className="text-sm font-medium text-gray-900 truncate">
                        {filter.label}
                      </span>
                    </div>

                    {/* Operator Selection */}
                    <ThemedSelect
                      value={filter.operator}
                      onChange={(e) =>
                        handleFilterChange(filter.id, 'operator', String(e.target.value))
                      }
                      className="px-3 py-2 text-sm border border-gray-300 bg-surface rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    >
                      {getAvailableFilterTypes(filter.type).map((type) => (
                        <option key={type.value} value={type.value}>
                          {type.label}
                        </option>
                      ))}
                    </ThemedSelect>

                    {/* Value Input */}
                    {renderFilterInput(filter)}

                    {/* Remove Button */}
                    <button
                      onClick={() => onRemoveDynamicFilter(filter.id)}
                      className="p-1 text-red-400 hover:text-red-600 focus:outline-none"
                      title="Remove filter"
                    >
                      <XMarkIcon className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-6 text-gray-500">
              <AdjustmentsHorizontalIcon className="h-8 w-8 mx-auto mb-2 text-gray-300" />
              <p className="text-sm">No dynamic filters added yet</p>
              <p className="text-xs">Click "Add Column Filter" to filter by any database column</p>
            </div>
          )}

          {/* Usage Tips */}
          <div className="bg-primary-50 border border-primary-200 rounded-lg p-3">
            <h6 className="text-sm font-medium text-primary-900 mb-2">Dynamic Filter Tips</h6>
            <ul className="text-xs text-primary-800 space-y-1">
              <li>• Add filters for any column in the LexiBridgeDataView database</li>
              <li>• Use category filters to quickly find related columns</li>
              <li>• Different data types have appropriate filter operators</li>
              <li>• "Is Empty" and "Is Not Empty" don't require values</li>
              <li>
                • Load Date, Is Amended, Import Source, Import Timestamp and Source File do not
                apply to the Amendment History tab
              </li>
              <li>• Combine with other filters for precise results</li>
            </ul>
          </div>
        </>
      )}
    </div>
  );
};

export default DynamicFilters;
